import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import ScreenVisualizer from "../../components/ScreenVisualizer";
import StatusChangeModal from "../../components/StatusChangeModal";
import { toast } from "sonner";
import { Download, Trash2 } from "lucide-react";
import { Calendar, AlertTriangle, CheckCircle2 } from "lucide-react";

const STATUSES = ["pending", "paid", "ordered", "ready_for_pickup", "ready_for_delivery", "completed"];
const ACTIVE_STATUSES = new Set(["pending", "paid", "ordered"]);

const STATUS_LABELS = {
  pending: "Pending payment",
  paid: "Paid",
  ordered: "Ordered",
  ready_for_pickup: "Ready for Pickup",
  ready_for_delivery: "Ready for Delivery",
  completed: "Completed",
};

/** Format a decimal inches value to a mixed-number string (36.5 → 36 1/2"). */
function fmtInches(v) {
  if (v === "" || v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const total16 = Math.round(n * 16);
  const whole = Math.floor(total16 / 16);
  const sx = total16 % 16;
  if (sx === 0) return `${whole}"`;
  const g = (a, b) => (b ? g(b, a % b) : a);
  const d = g(sx, 16);
  return `${whole > 0 ? whole + " " : ""}${sx / d}/${16 / d}"`;
}

/** Convert a stored ISO timestamp into a friendly "2h ago" / "3d ago" string. */
function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function StatusBadge({ status }) {
  const colors = {
    pending: "#fef3c7|#92400e",
    paid: "#dcfce7|#166534",
    ordered: "#dbeafe|#1e40af",
    ready_for_pickup: "#fef9c3|#854d0e",
    ready_for_delivery: "#e0e7ff|#3730a3",
    completed: "#d1fae5|#065f46",
  };
  const [bg, fg] = (colors[status] || "#e5e7eb|#374151").split("|");
  return (
    <span className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
      style={{ background: bg, color: fg }}>
      {STATUS_LABELS[status] || status.replace(/_/g, " ")}
    </span>
  );
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("active"); // 'active' | 'abandoned'
  const [recoveryRunning, setRecoveryRunning] = useState(false);
  // Pending status change awaiting admin confirmation in the notify modal.
  // Customer-facing transitions (paid/ordered/ready_*/completed) prompt the
  // editor; admin can still send/skip. `pending` updates apply silently.
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const NOTIFY_STATUSES = new Set(["paid", "ordered", "ready_for_pickup", "ready_for_delivery", "completed"]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, ab] = await Promise.all([
        api.get("/admin/orders" + (filter ? `?status=${filter}` : "")),
        api.get("/admin/orders/abandoned"),
      ]);
      setOrders(r.data);
      setAbandoned(ab.data || []);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const triggerCartRecovery = async () => {
    setRecoveryRunning(true);
    try {
      const r = await api.post("/admin/maintenance/run-cart-recovery");
      const { due, sent, failed, skipped } = r.data || {};
      toast.success(`Recovery emails: ${sent}/${due} sent · ${skipped} skipped · ${failed} failed`);
      load();
    } catch (e) {
      toast.error("Couldn't run cart recovery");
    } finally {
      setRecoveryRunning(false);
    }
  };

  // Orders waiting on Pro Measurement scheduling — fired when a customer adds
  // Pro Measurement after checkout. Dispatch should call/text to lock in a time.
  // Once `pro_measurement_appointment_at` is stamped, the order falls off the list.
  const waitingToSchedule = useMemo(
    () => orders
      .filter((o) => o.pro_measurement_scheduled_at
        && !o.pro_measurement_appointment_at
        && ACTIVE_STATUSES.has(o.status))
      .sort((a, b) => new Date(a.pro_measurement_scheduled_at) - new Date(b.pro_measurement_scheduled_at)),
    [orders],
  );

  const markProMeasurementScheduled = async (id) => {
    try {
      const r = await api.patch(`/admin/orders/${id}/pro-measurement/mark-scheduled`);
      toast.success("Pro Measurement marked as scheduled");
      load();
      if (selected?.id === id) setSelected(r.data);
    } catch (e) {
      toast.error("Couldn't mark as scheduled");
    }
  };

  const updateStatus = async (id, status) => {
    // Customer-facing transitions go through the notify modal.
    if (NOTIFY_STATUSES.has(status)) {
      const ord = orders.find((o) => o.id === id) || (selected?.id === id ? selected : null);
      if (ord) {
        setPendingStatusChange({ order: ord, newStatus: status });
        return;
      }
    }
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Status: ${status}`);
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteOrder = async (id) => {
    const order = orders.find((o) => o.id === id);
    const label = order ? `${order.order_number} (${order.customer?.email || "no email"})` : "this order";
    if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Order deleted");
      setSelected(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const exportCsv = async () => {
    try {
      // axios returns text body — wrap in Blob to trigger a save dialog. We
      // can't use a plain <a download> link because the endpoint requires
      // an Authorization header.
      const r = await api.get("/admin/orders.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `orders-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-[var(--brand-text)]">Orders</h1>
        <div className="flex items-center gap-2">
          <button data-testid="export-csv-btn" onClick={exportCsv}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold border-2 border-[var(--brand-border)] hover:brand-text">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <select data-testid="orders-filter"
            value={filter} onChange={(e) => setFilter(e.target.value)}
            disabled={view === "abandoned"}
            className="rounded-full border-2 border-[var(--brand-border)] bg-white px-4 py-2 text-sm font-semibold disabled:opacity-50">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>)}
          </select>
        </div>
      </div>

      {/* Active / Abandoned tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button
          data-testid="tab-active"
          onClick={() => setView("active")}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
            view === "active"
              ? "brand-bg text-white"
              : "bg-white border border-[var(--brand-border)] text-[var(--brand-text)] hover:border-[var(--brand-green)]"
          }`}
        >
          Active <span className="ml-1 opacity-70">({orders.length})</span>
        </button>
        <button
          data-testid="tab-abandoned"
          onClick={() => setView("abandoned")}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
            view === "abandoned"
              ? "text-white"
              : "bg-white border border-[var(--brand-border)] text-[var(--brand-text)] hover:border-amber-400"
          }`}
          style={view === "abandoned" ? { background: "#9a3412" } : undefined}
        >
          Abandoned carts <span className="ml-1 opacity-70">({abandoned.length})</span>
        </button>
        {view === "abandoned" && abandoned.length > 0 && (
          <button
            data-testid="run-cart-recovery-btn"
            onClick={triggerCartRecovery}
            disabled={recoveryRunning}
            className="ml-auto px-3 py-2 rounded-full text-xs font-semibold border-2 border-amber-400 text-amber-800 hover:bg-amber-50 disabled:opacity-50"
            title="Send the recovery email to all eligible abandoned carts now"
          >
            {recoveryRunning ? "Sending…" : "Send recovery emails"}
          </button>
        )}
      </div>

      {view === "active" && waitingToSchedule.length > 0 && (
        <div
          className="rounded-2xl border-2 p-4 mb-5"
          style={{ background: "#fff7ed", borderColor: "#fdba74" }}
          data-testid="pro-measure-widget"
        >
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5" style={{ color: "#9a3412" }} />
            <h2 className="font-bold" style={{ color: "#9a3412" }}>
              {waitingToSchedule.length} customer{waitingToSchedule.length === 1 ? "" : "s"} waiting to schedule Pro Measurement
            </h2>
            <span
              className="ml-auto text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: "#9a3412" }}
            >
              oldest: {timeAgo(waitingToSchedule[0].pro_measurement_scheduled_at)}
            </span>
          </div>
          <div className="space-y-1.5">
            {waitingToSchedule.slice(0, 5).map((o) => (
              <button
                key={o.id}
                type="button"
                data-testid={`pro-measure-item-${o.id}`}
                onClick={() => setSelected(o)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-white border border-[#fed7aa] hover:border-[#f97316] text-left"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[var(--brand-text)] text-sm truncate">
                    {o.order_number} · {o.customer?.name}
                  </p>
                  <p className="text-xs muted-text truncate">
                    {o.customer?.phone} · added {timeAgo(o.pro_measurement_scheduled_at)}
                  </p>
                </div>
                <span className="text-xs font-semibold brand-text shrink-0">View →</span>
              </button>
            ))}
            {waitingToSchedule.length > 5 && (
              <p className="text-[11px] muted-text px-2 pt-1">
                + {waitingToSchedule.length - 5} more — filter by Paid to see all.
              </p>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <p className="muted-text">Loading…</p>
      ) : view === "abandoned" ? (
        abandoned.length === 0 ? (
          <div className="option-card text-center muted-text" data-testid="abandoned-empty">
            🎉 No abandoned carts. Customers are finishing what they start.
          </div>
        ) : (
          <div className="space-y-2" data-testid="abandoned-list">
            <p className="text-xs muted-text mb-2">
              Customers who reached checkout but didn't pay. We email them a "finish in 1 click" link automatically ~24h after abandonment. Carts older than 30 days are skipped.
            </p>
            {abandoned.map((o) => {
              const ageMs = Date.now() - new Date(o.created_at).getTime();
              const hours = Math.floor(ageMs / 3_600_000);
              const sentAt = o.cart_recovery_sent_at;
              return (
                <div key={o.id}
                  className="bg-white border border-[var(--brand-border)] rounded-2xl p-4"
                  data-testid={`abandoned-row-${o.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-[var(--brand-text)]">{o.order_number}</p>
                      <p className="text-sm muted-text truncate">
                        {o.customer?.name} · {o.customer?.email}
                      </p>
                      <p className="text-xs muted-text">
                        {o.product_name} ×{o.quantity} · ${o.pricing?.total?.toFixed(2)} · started {hours < 1 ? "<1h" : `${hours}h`} ago
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {sentAt ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                            style={{ background: "#dcfce7", color: "#166534" }}>
                            Recovery email sent {timeAgo(sentAt)}
                          </span>
                        ) : hours >= 24 ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                            style={{ background: "#fef3c7", color: "#92400e" }}>
                            Eligible for recovery
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                            style={{ background: "#e0e7ff", color: "#3730a3" }}>
                            Too fresh ({24 - hours}h to wait)
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete abandoned cart ${o.order_number}? This can't be undone.`)) return;
                        try {
                          await api.delete(`/admin/orders/${o.id}`);
                          toast.success("Deleted");
                          load();
                        } catch { toast.error("Delete failed"); }
                      }}
                      data-testid={`abandoned-delete-${o.id}`}
                      className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50"
                      title="Delete cart"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : orders.length === 0 ? (
        <div className="option-card text-center muted-text">No orders yet.</div>
      ) : (
        <div className="space-y-2">
          {orders.map((o) => {
            const isCatalog = o.order_type === "catalog";
            const catalogQty = isCatalog
              ? (o.catalog_items || []).reduce((s, it) => s + (it.quantity || 0), 0)
              : null;
            const catalogLabel = isCatalog
              ? (o.catalog_items || []).map((it) => it.name).join(", ") || "Parts order"
              : null;
            return (
            <div key={o.id} className="bg-white border border-[var(--brand-border)] rounded-2xl p-4"
              data-testid={`order-row-${o.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-[var(--brand-text)] truncate">{o.order_number || o.id}</p>
                  <p className="text-sm muted-text truncate">
                    {o.customer?.name || "—"} · {isCatalog ? catalogLabel : (o.product_name || "(no product)")} ×{isCatalog ? catalogQty : (o.quantity ?? "?")} · ${(o.pricing?.total ?? 0).toFixed(2)}
                  </p>
                  <p className="text-xs muted-text flex items-center gap-2">
                    <span>{o.created_at ? new Date(o.created_at).toLocaleString() : ""}</span>
                    {isCatalog && (
                      <span
                        data-testid={`catalog-badge-${o.id}`}
                        className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase"
                        style={{ background: "#e0e7ff", color: "#3730a3" }}
                      >
                        Parts {o.fulfillment === "delivery" ? "· delivery" : "· pickup"}
                      </span>
                    )}
                    {!isCatalog && o.measurements_required && !o.measurements_completed && (
                      <span data-testid={`pending-measure-${o.id}`}
                        className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase"
                        style={{ background: "#fef3c7", color: "#92400e" }}>
                        Awaiting measurements
                      </span>
                    )}
                    {!isCatalog && !o.measurements_required && (
                      <span data-testid={`pro-measure-${o.id}`}
                        className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase"
                        style={{ background: "#dbeafe", color: "#1e40af" }}>
                        Pro measure
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={o.status} />
                  <button onClick={() => setSelected(o)} data-testid={`view-order-${o.id}`}
                    className="text-sm font-semibold brand-text underline">View</button>
                  <button
                    data-testid={`active-delete-${o.id}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm(`Delete order ${o.order_number || o.id}? This can't be undone.`)) return;
                      try {
                        await api.delete(`/admin/orders/${o.id}`);
                        toast.success("Deleted");
                        load();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Delete failed");
                      }
                    }}
                    title="Delete order"
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50 text-sm"
                  >×</button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center px-5"
          onClick={() => setSelected(null)} data-testid="order-detail-overlay">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()} data-testid="order-detail-modal">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="text-xs uppercase muted-text">Order</p>
                <h2 className="text-xl font-bold">{selected.order_number}</h2>
              </div>
              <StatusBadge status={selected.status} />
            </div>

            <div className="mb-4">
              <p className="text-xs uppercase muted-text">Customer</p>
              <p className="font-semibold">{selected.customer.name}</p>
              <p className="text-sm muted-text">{selected.customer.email} · {selected.customer.phone}</p>
              <p className="text-sm muted-text">
                {selected.customer.address_line1}{selected.customer.address_line2 ? `, ${selected.customer.address_line2}` : ""}, {selected.customer.city}, {selected.customer.state} {selected.customer.zip_code}
              </p>
            </div>

            {selected.pro_measurement_scheduled_at && !selected.pro_measurement_appointment_at && ACTIVE_STATUSES.has(selected.status) && (
              <div
                className="rounded-xl p-3 mb-4 flex items-start gap-2"
                style={{ background: "#fff7ed", border: "1px solid #fdba74" }}
                data-testid="order-pro-measure-flag"
              >
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#9a3412" }} />
                <div className="text-xs flex-1" style={{ color: "#9a3412" }}>
                  <p className="font-bold">Waiting on Pro Measurement scheduling</p>
                  <p>Customer added Pro Measurement {timeAgo(selected.pro_measurement_scheduled_at)}. Call/text to lock in a time.</p>
                  <button
                    data-testid="mark-pro-scheduled-btn"
                    type="button"
                    onClick={() => markProMeasurementScheduled(selected.id)}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#9a3412] text-white hover:bg-[#7c2d12]"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Mark scheduled
                  </button>
                </div>
              </div>
            )}

            {selected.pro_measurement_appointment_at && (
              <div
                className="rounded-xl p-3 mb-4 flex items-start gap-2"
                style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}
                data-testid="order-pro-measure-scheduled"
              >
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 brand-text" />
                <div className="text-xs brand-text">
                  <p className="font-bold">Pro Measurement scheduled</p>
                  <p>Marked scheduled {timeAgo(selected.pro_measurement_appointment_at)}.</p>
                </div>
              </div>
            )}

            <div className="mb-4">
              {selected.order_type === "catalog" ? (
                <>
                  <p className="text-xs uppercase muted-text">
                    Parts order
                    {selected.fulfillment && (
                      <span className="ml-2 brand-text font-semibold">· {selected.fulfillment === "delivery" ? "Local delivery" : "Pickup at shop"}</span>
                    )}
                  </p>
                  <ul className="text-sm mt-2 space-y-2">
                    {(selected.catalog_items || []).map((it, idx) => (
                      <li key={idx} className="border border-[var(--brand-border)] rounded-xl p-3 flex items-start justify-between gap-3" data-testid={`admin-catalog-item-${idx}`}>
                        <div className="min-w-0">
                          <p className="font-semibold text-[var(--brand-text)]">{it.name} ×{it.quantity}</p>
                          {it.sku && <p className="text-xs muted-text">SKU: {it.sku}</p>}
                        </div>
                        <span className="font-semibold text-[var(--brand-text)] tabular-nums">${(it.line_total ?? 0).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p className="text-xs uppercase muted-text">
                    Items ({selected.product_name} ×{selected.quantity})
                    {selected.mesh_type && selected.mesh_type !== "standard" && (
                      <span className="ml-2 brand-text font-semibold">· mesh: {selected.mesh_type.replace("_", " ")}</span>
                    )}
                  </p>
                  {(selected.items || []).length === 0 ? (
                    <p className="text-sm muted-text mt-1 italic">Customer has not submitted measurements yet.</p>
                  ) : (
                    <ul className="text-sm mt-1 space-y-3">
                      {(selected.items || []).map((it, idx) => {
                        const isWindow = selected.product_slug === "window-screen";
                        return (
                          <li key={idx} className="border border-[var(--brand-border)] rounded-xl p-3" data-testid={`admin-item-${idx}`}>
                            <div className="flex justify-between mb-2">
                              <span className="font-semibold text-[var(--brand-text)]">
                                Item {idx + 1}: {fmtInches(it.width)} × {fmtInches(it.height)}
                              </span>
                              {it.petscreen && <span className="text-xs font-semibold brand-text">PetScreen</span>}
                            </div>
                            {isWindow && (
                              <div className="-mx-1 mb-2">
                                <ScreenVisualizer
                                  width={it.width}
                                  height={it.height}
                                  springSide={it.spring_side || ""}
                                  hasSprings={it.has_springs !== false}
                                  hasPullTabs={it.has_pull_tabs !== false}
                                />
                              </div>
                            )}
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs muted-text">
                              <span>Frame: <strong className="text-[var(--brand-text)]">{it.frame_color}</strong></span>
                              {it.spring_side && (
                                <span>Springs: <strong className="text-[var(--brand-text)]">
                                  {it.spring_side === "unsure" ? "not sure" : it.spring_side} edge
                                </strong></span>
                              )}
                              {it.has_springs === false && <span className="text-amber-700">no springs</span>}
                              {it.has_pull_tabs === false && <span className="text-amber-700">no pull tabs</span>}
                            </div>
                            {it.notes && <p className="text-xs muted-text mt-1">Note: {it.notes}</p>}
                            {it.photo_path && (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${it.photo_path}`}
                                alt="item" className="mt-2 rounded-lg max-h-32 object-cover" />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}
            </div>

            <div className="mb-4">
              <p className="text-xs uppercase muted-text">Pricing</p>
              <div className="text-sm space-y-1 mt-1">
                <div className="flex justify-between"><span>Items</span><span>${selected.pricing.items_subtotal.toFixed(2)}</span></div>
                {selected.pricing.pro_measurement_fee > 0 && <div className="flex justify-between"><span>Pro measure</span><span>${selected.pricing.pro_measurement_fee.toFixed(2)}</span></div>}
                {selected.pricing.delivery_fee > 0 && <div className="flex justify-between"><span>Delivery</span><span>${selected.pricing.delivery_fee.toFixed(2)}</span></div>}
                {selected.pricing.install_fee > 0 && <div className="flex justify-between"><span>Install</span><span>${selected.pricing.install_fee.toFixed(2)}</span></div>}
                {selected.pricing.bulk_discount > 0 && <div className="flex justify-between brand-text"><span>Bulk Savings</span><span>−${selected.pricing.bulk_discount.toFixed(2)}</span></div>}
                {selected.pricing.white_glove_fee > 0 && <div className="flex justify-between"><span>Measurement & Perfect Fit Guarantee</span><span>${selected.pricing.white_glove_fee.toFixed(2)}</span></div>}
                {selected.pricing.tax > 0 && <div className="flex justify-between"><span>Sales tax ({(selected.pricing.tax_rate * 100).toFixed(2)}% · {selected.pricing.tax_state})</span><span>${selected.pricing.tax.toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold text-base border-t border-[var(--brand-border)] pt-2 mt-2"><span>Total</span><span>${selected.pricing.total.toFixed(2)}</span></div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase muted-text mb-1">Update status</p>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button key={s}
                    data-testid={`set-status-${s}`}
                    disabled={s === selected.status}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${
                      s === selected.status
                        ? "brand-bg text-white border-transparent cursor-default"
                        : "border-[var(--brand-border)] hover:border-[var(--brand-green)]"
                    }`}>
                    {STATUS_LABELS[s] || s.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </div>

            <CostPanel order={selected} onSaved={(updated) => {
              setSelected(updated);
              setOrders((rows) => rows.map((r) => r.id === updated.id ? updated : r));
            }} />

            <SupplierSmsPanel order={selected} onSent={(updated) => {
              setSelected(updated);
              setOrders((rows) => rows.map((r) => r.id === updated.id ? updated : r));
            }} />

            <UpsellChargesPanel
              order={selected}
              onRefunded={(updated) => {
                setSelected(updated);
                setOrders((rows) => rows.map((r) => r.id === updated.id ? updated : r));
              }}
            />

            <div className="mt-5 pt-4 border-t border-[var(--brand-border)] flex gap-3">
              <button onClick={() => setSelected(null)} data-testid="close-order-detail"
                className="flex-1 py-2 rounded-full border-2 border-[var(--brand-border)] font-semibold">
                Close
              </button>
              <button data-testid={`delete-order-${selected.id}`}
                onClick={() => deleteOrder(selected.id)}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border-2 border-red-200 text-red-700 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" /> Delete order
              </button>
            </div>
          </div>
        </div>
      )}
      {pendingStatusChange && (
        <StatusChangeModal
          order={pendingStatusChange.order}
          newStatus={pendingStatusChange.newStatus}
          onClose={() => setPendingStatusChange(null)}
          onApplied={(updated) => {
            setPendingStatusChange(null);
            if (updated) {
              setOrders((rows) => rows.map((r) => r.id === updated.id ? updated : r));
              if (selected?.id === updated.id) setSelected(updated);
            }
            load();
          }}
        />
      )}
    </AdminLayout>
  );
}



/**
 * Admin-only cost & profit panel inside the order detail modal.
 *
 * - `admin_cost` is editable cost-of-goods (materials + labor + overhead) the
 *   admin enters by hand.
 * - Profit and margin auto-calc against `pricing.total` so the admin can see
 *   the unit economics at a glance without leaving the order.
 * - Customer NEVER sees these fields (they live behind admin auth and are
 *   never returned on `/api/orders/{id}`).
 */
function CostPanel({ order, onSaved }) {
  const [cost, setCost] = useState(
    order.admin_cost != null ? String(order.admin_cost) : ""
  );
  const [notes, setNotes] = useState(order.admin_cost_notes || "");
  const [saving, setSaving] = useState(false);

  // Reset when modal switches between orders.
  useEffect(() => {
    setCost(order.admin_cost != null ? String(order.admin_cost) : "");
    setNotes(order.admin_cost_notes || "");
  }, [order.id, order.admin_cost, order.admin_cost_notes]);

  const total = Number(order.pricing?.total || 0);
  const costNum = Number(cost) || 0;
  const profit = total - costNum;
  const marginPct = total > 0 ? (profit / total) * 100 : 0;
  const marginColor = profit > 0 ? "var(--brand-green)" : profit < 0 ? "#dc2626" : "#6b7280";
  const dirty =
    String(order.admin_cost ?? "") !== String(cost === "" ? "" : Number(cost)) ||
    (order.admin_cost_notes || "") !== notes;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/orders/${order.id}/cost`, {
        admin_cost: cost === "" ? 0 : Number(cost),
        admin_cost_notes: notes,
      });
      toast.success("Cost saved");
      onSaved?.(r.data);
    } catch {
      toast.error("Couldn't save cost");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-cost-panel"
      className="mt-5 rounded-2xl p-4"
      style={{ background: "#fef9c3", border: "1px solid #fde047" }}>
      <p className="text-[11px] uppercase tracking-wider font-bold mb-2"
        style={{ color: "#854d0e" }}>
        Admin only · Cost &amp; profit
      </p>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <label className="text-xs font-semibold muted-text">Cost of goods ($)</label>
          <input
            data-testid="admin-cost-input"
            type="number" inputMode="decimal" min="0" step="0.01"
            value={cost} onChange={(e) => setCost(e.target.value)}
            placeholder="0.00"
            className="rounded-input mt-1"
          />
        </div>
        <div>
          <label className="text-xs font-semibold muted-text">Revenue (locked)</label>
          <p className="mt-1 px-3 py-2 rounded-2xl bg-white border-2 border-[var(--brand-border)] font-semibold tabular-nums">
            ${total.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
        <div>
          <p className="text-xs font-semibold muted-text">Profit</p>
          <p data-testid="admin-profit-value" className="text-xl font-extrabold tabular-nums" style={{ color: marginColor }}>
            ${profit.toFixed(2)}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold muted-text">Margin</p>
          <p data-testid="admin-margin-value" className="text-xl font-extrabold tabular-nums" style={{ color: marginColor }}>
            {marginPct.toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="mt-3">
        <label className="text-xs font-semibold muted-text">Notes (materials, labor, anything)</label>
        <textarea
          data-testid="admin-cost-notes"
          value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={2} maxLength={2000}
          placeholder="e.g. Frame $14 · mesh $6 · install 1.5h"
          className="w-full rounded-2xl border-2 border-[var(--brand-border)] bg-white px-3 py-2 text-sm mt-1 resize-none"
        />
      </div>

      <button
        data-testid="admin-cost-save-btn"
        onClick={save}
        disabled={!dirty || saving}
        className="mt-3 w-full py-2 rounded-full font-semibold text-sm brand-bg text-white disabled:opacity-50">
        {saving ? "Saving…" : "Save cost"}
      </button>
    </div>
  );
}


function UpsellChargesPanel({ order, onRefunded }) {
  const charges = order.upsell_charges || [];
  const proCancelFlag = !!order.pro_measurement_cancel_appt_flag;
  const [working, setWorking] = useState(null); // `${chargeId}|${item}` while processing

  if (!charges.length && !proCancelFlag) return null;

  const refunded = (c, key) => {
    for (const r of c.refunds || []) {
      if ((r.items_refunded || []).includes(key)) return r;
    }
    return null;
  };

  const refundItem = async (charge, key) => {
    if (!window.confirm(`Refund ${key.replaceAll("_"," ")} ($${charge.items.find(i => i.key === key)?.amount.toFixed(2)})? This issues a Stripe refund immediately.`)) return;
    setWorking(`${charge.id}|${key}`);
    try {
      const r = await api.post(`/admin/orders/${order.id}/refund-upsell`, {
        charge_id: charge.id,
        items: [key],
        reason: "Customer-requested upsell refund",
      });
      toast.success(`Refunded $${r.data.refund_amount.toFixed(2)}`);
      // Refresh the order doc.
      const ord = await api.get(`/admin/orders/${order.id}`);
      onRefunded(ord.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    } finally {
      setWorking(null);
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-[var(--brand-border)]" data-testid="admin-upsell-charges">
      <p className="text-xs uppercase muted-text mb-2">Add-on charges</p>
      {proCancelFlag && (
        <div className="mb-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-3 text-xs flex items-start gap-2" data-testid="pro-cancel-flag">
          <span>⚠️</span>
          <div className="flex-1">
            <p className="font-bold text-amber-900">Pro Measurement was refunded — cancel any booked appointment.</p>
            <p className="text-amber-800 mt-0.5">Once you've cancelled the appointment, clear this flag.</p>
          </div>
          <button
            data-testid="clear-pro-cancel-flag"
            onClick={async () => {
              try {
                await api.patch(`/admin/orders/${order.id}/clear-pro-cancel-flag`);
                const ord = await api.get(`/admin/orders/${order.id}`);
                onRefunded(ord.data);
              } catch { toast.error("Failed to clear"); }
            }}
            className="text-xs font-semibold underline shrink-0"
          >Clear</button>
        </div>
      )}
      <div className="space-y-2">
        {charges.length === 0 && <p className="text-xs muted-text">No upsell charges yet.</p>}
        {charges.map((c) => (
          <div key={c.id} className="rounded-2xl border border-[var(--brand-border)] p-3 bg-white">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="text-xs muted-text">
                {c.created_at ? new Date(c.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : ""}
                <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
                  style={{
                    background: c.status === "paid" ? "#dcfce7" : c.status === "refunded" ? "#fee2e2" : "#fef3c7",
                    color: c.status === "paid" ? "#166534" : c.status === "refunded" ? "#991b1b" : "#92400e",
                  }}>{c.status?.replaceAll("_", " ")}</span>
              </div>
              <div className="text-xs font-bold tabular-nums">${c.amount_total?.toFixed(2)}</div>
            </div>
            <div className="space-y-1">
              {(c.items || []).map((it) => {
                const r = refunded(c, it.key);
                const wk = `${c.id}|${it.key}`;
                return (
                  <div key={it.key} className="flex items-center justify-between text-xs">
                    <span>{it.label} <span className="muted-text tabular-nums">· ${it.amount.toFixed(2)}</span></span>
                    {r ? (
                      <span className="text-[10px] text-red-700">Refunded {new Date(r.refunded_at).toLocaleDateString()}</span>
                    ) : c.status === "paid" || c.status === "partially_refunded" ? (
                      <button
                        data-testid={`refund-${c.id}-${it.key}`}
                        onClick={() => refundItem(c, it.key)}
                        disabled={working === wk}
                        className="text-[10px] font-bold text-red-700 underline disabled:opacity-50"
                      >{working === wk ? "Refunding…" : "Refund"}</button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function SupplierSmsPanel({ order, onSent }) {
  const [sending, setSending] = useState(false);
  const last = order.supplier_sms || {};
  const isCatalog = order.order_type === "catalog";
  if (isCatalog) return null;

  const send = async () => {
    if (!order.measurements_completed) {
      if (!window.confirm("Measurements aren't completed yet. Send to Active anyway?")) return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/orders/${order.id}/send-to-supplier`);
      if (r.data.sent) toast.success(`Sent to ${r.data.to}`);
      else if (r.data.stubbed) toast.info("Saved (Twilio toll-free still pending — message logged on order).");
      else toast.warning(`Failed: ${r.data.twilio_error || "unknown"}`);
      const ord = await api.get(`/admin/orders/${order.id}`);
      onSent(ord.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-[var(--brand-border)]" data-testid="supplier-sms-panel">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-xs uppercase muted-text">Supplier dispatch</p>
        <button
          data-testid="send-to-active-btn"
          onClick={send}
          disabled={sending}
          className="brand-bg text-white text-xs font-semibold px-4 py-2 rounded-full disabled:opacity-50"
        >{sending ? "Sending…" : "Send order to Active"}</button>
      </div>
      {last.last_at ? (
        <p className="text-[11px] muted-text">
          Last sent {new Date(last.last_at).toLocaleString()} → {last.last_to}
          <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
            style={{
              background: last.last_status === "sent" ? "#dcfce7" : last.last_status === "failed" ? "#fee2e2" : "#fef3c7",
              color: last.last_status === "sent" ? "#166534" : last.last_status === "failed" ? "#991b1b" : "#92400e",
            }}>{last.last_status}</span>
        </p>
      ) : (
        <p className="text-[11px] muted-text">Not sent yet. Make sure measurements are filled in first.</p>
      )}
    </div>
  );
}
